/**
 * EO «Конфигуратор → Справочники» (screen-21, A44/DICT-2) — панель: две
 * сворачиваемые секции (Расходники открыты, Ресурсы свёрнуты — локальный
 * state, НЕ staff_prefs), фильтры Филиал/Квест из живых данных (Д9; демо-
 * хардкод фильтра «20Б» эталона не переносится — Д1), формы создания с
 * client-id, удаление Д6 (K=0 — undo-снекбар + deferred DELETE keepalive;
 * K>0 и 409 сервера — CfgModal danger «Всё равно удалить» → ?force=1),
 * PTR, скелетон, типсы «?» (тексты эталона J:399-425 дословно — DictTips).
 * Dirty-guard суб-таба не нужен — модель Услуг (Д4). Аккордеон-карточки —
 * DictConsCard / DictResCard (DICT-3): «Сохранить» карточки → patchRow +
 * flash строки + refetch (use_count пересчитывает сервер).
 */
import { useMemo, useRef, useState } from "react";
import type { DictConsumable, DictResource } from "../../api/dictTypes";
import { PullHud, useUndoSnackbar } from "../../components/shared";
import { FLT0, matchCons, matchRes, type DictFlt } from "../../hooks/dictFilters";
import { useAppData } from "../../hooks/useAppData";
import { useAppReload, usePullToRefresh } from "../../hooks/usePullToRefresh";
import { useDictsAdmin, type DictKind } from "../../hooks/useDictsAdmin";
import { CfgModal } from "./EoConfigModal";
import { TipBox } from "./PriceRow";
import { DictConsCard, type ConsSt } from "./DictConsCard";
import { DictResCard, type ResSt } from "./DictResCard";
import { DictSection, type DictFormSt, type DictRow } from "./DictSection";
import { DictTips } from "./DictTips";
import { BranchDaysSection } from "./BranchDaysSection";
import { reloadBranchDays } from "../../hooks/useBranchDays";
import { UNITS } from "./DictUnitCtl";
import cfg from "./config.module.css";
import css from "./dicts.module.css";

/** Client-id — один на сессию формы (идемпотентный повтор «Создать», A36:
 *  паттерн V1-префиксов con_/r_ + newSvcId). */
const newId = (kind: DictKind): string =>
  `${kind === "c" ? "con" : "r"}_${Date.now().toString(36)}${Math.floor(Math.random() * 1296).toString(36)}`;
const form0 = (kind: DictKind): DictFormSt =>
  ({ id: newId(kind), n: "", u: "шт", custom: false, focus: true });

export function EoConfigDicts() {
  const app = useAppData();
  const sa = useDictsAdmin();
  const snack = useUndoSnackbar();
  const [secOpen, setSecOpen] = useState<Record<DictKind, boolean>>({ c: true, r: false });
  const [flt, setFlt] = useState<Record<DictKind, DictFlt>>({ c: FLT0, r: FLT0 });
  const [formSt, setFormSt] = useState<Record<DictKind, DictFormSt | null>>({ c: null, r: null });
  const [busy, setBusy] = useState<Record<DictKind, boolean>>({ c: false, r: false });
  const [editId, setEditId] = useState<string | null>(null);
  const [tip, setTip] = useState<{ k: DictKind; anchor: HTMLElement } | null>(null);
  /* Буферы правок карточек id→St (эталон: module-map editSt J:197-199) —
     collapse секции/фильтр/смена editId размонтируют карточку, правки живут;
     буфер чистят сама карточка (сейв/«Отмена») и удаление строки. */
  const bufC = useRef<Record<string, ConsSt | undefined>>({});
  const bufR = useRef<Record<string, ResSt | undefined>>({});
  const scRef = useRef<HTMLDivElement | null>(null);
  const formRefC = useRef<HTMLDivElement | null>(null);
  const formRefR = useRef<HTMLDivElement | null>(null);
  const reloadApp = useAppReload();
  const ptr = usePullToRefresh(() => { sa.refetch(); reloadBranchDays(); return reloadApp(); });
  const setScroll = (el: HTMLDivElement | null) => { scRef.current = el; ptr.ref(el); };

  const questBr = useMemo(
    () => Object.fromEntries(app.quests.map((q) => [q.id, q.branch_id])), [app.quests]);
  const teaBr = useMemo(
    () => Object.fromEntries(app.teaRooms.map((t) => [t.id, t.branch_id])), [app.teaRooms]);
  const activeQuests = useMemo(() => app.quests.filter((q) => q.active !== 0), [app.quests]);
  const branches = useMemo(() => app.branches.filter((b) => b.active !== 0), [app.branches]);
  const activeTeas = useMemo(() => app.teaRooms.filter((t) => t.active !== 0), [app.teaRooms]);
  const activeSvcs = useMemo(() => app.masterClasses.filter((m) => m.active !== 0), [app.masterClasses]);
  const consRows = useMemo(
    () => sa.cons.filter((x) => matchCons(x, flt.c, sa.consLinks, questBr, teaBr)),
    [sa.cons, sa.consLinks, flt.c, questBr, teaBr]);
  const resRows = useMemo(
    () => sa.ress.filter((x) => matchRes(x, flt.r, sa.resLinks, questBr)),
    [sa.ress, sa.resLinks, flt.r, questBr]);

  /* Д14: выбор фильтра принудительно разворачивает секцию. */
  const setFltK = (k: DictKind, f: DictFlt) => {
    setFlt((o) => ({ ...o, [k]: f }));
    setSecOpen((o) => (o[k] ? o : { ...o, [k]: true }));
  };
  /* Д12: «+» свёрнутой секции — развернуть + свежая форма; черновик не живёт. */
  const plus = (k: DictKind) => {
    setSecOpen((o) => ({ ...o, [k]: true }));
    setBusy((o) => ({ ...o, [k]: false }));
    setFormSt((o) => ({ ...o, [k]: form0(k) }));
  };
  const cancelForm = (k: DictKind) => {
    setBusy((o) => ({ ...o, [k]: false }));
    setFormSt((o) => ({ ...o, [k]: null }));
  };
  const submit = (k: DictKind) => {
    const st = formSt[k];
    if (!st || busy[k] || !st.n.trim() || (k === "c" && !st.u.trim())) return;
    setBusy((o) => ({ ...o, [k]: true }));
    const done = k === "c"
      ? sa.createCons(st.id, st.n.trim(), st.u.trim())
      : sa.createRes(st.id, st.n.trim());
    void done.then((id) => {
      setBusy((o) => ({ ...o, [k]: false }));
      if (id) {
        setFormSt((o) => ({ ...o, [k]: null }));
        /* Д5: сброс фильтров секции — иначе новая строка невидима, flash теряется. */
        setFlt((o) => ({ ...o, [k]: FLT0 }));
      }
    });
  };
  /* A43.1: свайп-дубликат копирует ТОЛЬКО имя+единицу; editId НЕ трогаем —
     открытая карточка сосуществует с формой копии (эталон J:349-352). */
  const dup = (k: DictKind, row: DictRow) => {
    const unit = k === "c" ? (row as DictConsumable).unit || "шт" : "шт";
    setFormSt((o) => ({ ...o, [k]: {
      id: newId(k), n: `${row.name} (копия)`.slice(0, 200), u: unit,
      custom: k === "c" && !!unit && !UNITS.includes(unit), focus: false,
    } }));
    requestAnimationFrame(() => { // скролл панели к форме (эталон: offsetTop-60)
      const el = (k === "c" ? formRefC : formRefR).current;
      const sc = scRef.current;
      if (el && sc) sc.scrollTop += el.getBoundingClientRect().top - sc.getBoundingClientRect().top - 60;
    });
  };
  /* Закрыть карточку строки и убить её буфер правок — эталонный kill
     (J:115-126): зовётся ТОЛЬКО когда удаление решено (K=0 сразу; K>0 —
     после «Всё равно удалить»), отменённый guard правки не теряет. */
  const killRow = (k: DictKind, id: string) => {
    setEditId((e) => (e === id ? null : e));
    delete (k === "c" ? bufC : bufR).current[id];
  };
  /* Д6: K=0 — hide + снекбар (deferred DELETE); K>0 — DELETE без force,
     guard-модалку поднимет 409 сервера (uses/count считает сервер);
     карточка/буфер при K>0 не трогаются до подтверждения. */
  const del = (k: DictKind, row: DictRow) => {
    if (row.use_count > 0) { sa.tryDelete(k, row.id, row.name); return; }
    killRow(k, row.id);
    sa.hide(row.id);
    snack.show({
      label: `${k === "c" ? "Расходник" : "Ресурс"} «${row.name}» удалён`,
      onUndo: () => sa.unhide(row.id),
      onExpire: () => sa.removeNow(k, row.id, row.name),
    });
  };
  /* «Сохранить» карточки: строка списка патчится optimistic, флэшится,
     use_count пересчитывает refetch (useDictCard списки не обновляет). */
  const cardSaved = (k: DictKind, id: string, patch: Partial<DictConsumable & DictResource>) => {
    sa.patchRow(k, id, patch);
    setEditId(null);
    sa.flashNow(`${k}:${id}`);
    sa.refetch();
  };
  const renderCard = (k: DictKind) => (row: DictRow) => (k === "c" ? (
    <DictConsCard row={row as DictConsumable} quests={activeQuests} teas={activeTeas} branches={branches}
      services={activeSvcs} buf={bufC.current} onClose={() => setEditId(null)}
      onSaved={(patch) => cardSaved("c", row.id, patch)} onFields={(f) => sa.patchRow("c", row.id, f)} />
  ) : (
    <DictResCard row={row as DictResource} branches={branches} quests={activeQuests}
      services={activeSvcs} buf={bufR.current} onClose={() => setEditId(null)}
      onSaved={(patch) => cardSaved("r", row.id, patch)} />
  ));

  if (!sa.loaded) {
    return (
      <div className={cfg.scroll} ref={setScroll}>
        <PullHud pull={ptr.pull} pulling={ptr.pulling} refreshing={ptr.refreshing} />
        {sa.loadErr ? (
          <div className={cfg.errHint} style={{ margin: "8px 10px 0" }}>
            Справочники не загрузились — потяните вниз, чтобы повторить.
          </div>
        ) : null}
        <div className={cfg.skel}>
          <div className={`${cfg.sk} ${cfg.skTitle}`} />
          {[0, 1, 2, 3, 4, 5, 6].map((i) => <div key={i} className={`${cfg.sk} ${cfg.skTea}`} />)}
        </div>
      </div>
    );
  }

  const secProps = (k: DictKind) => ({
    kind: k,
    open: secOpen[k],
    onToggle: () => setSecOpen((o) => ({ ...o, [k]: !o[k] })),
    flt: flt[k],
    onFlt: (f: DictFlt) => setFltK(k, f),
    branches,
    quests: activeQuests,
    /* Эталон J:403: открытый типс закрывается ЛЮБЫМ «?» (и своим, и чужим),
       новый при этом не открывается (TipBox не гасится по [data-qmark]). */
    onTip: (anchor: HTMLElement) => setTip((t) => (t ? null : { k, anchor })),
    formSt: formSt[k],
    formRef: k === "c" ? formRefC : formRefR,
    busy: busy[k],
    onPlus: () => plus(k),
    onForm: (st: DictFormSt) => setFormSt((o) => ({ ...o, [k]: st })),
    onSubmit: () => submit(k),
    onCancelForm: () => cancelForm(k),
    flash: sa.flash,
    editId,
    renderCard: renderCard(k),
    onOpenRow: (row: DictRow) => setEditId(row.id),
    onCloseRow: () => setEditId(null),
    onDup: (row: DictRow) => dup(k, row),
    onDel: (row: DictRow) => del(k, row),
  });
  const g = sa.guard;

  return (
    <>
      <div className={cfg.scroll} ref={setScroll}>
        <PullHud pull={ptr.pull} pulling={ptr.pulling} refreshing={ptr.refreshing} />
        {sa.loadErr ? (
          <div className={cfg.errHint} style={{ margin: "0 8px" }}>
            Часть данных не загрузилась — потяните вниз, чтобы обновить.
          </div>
        ) : null}
        {sa.hint ? <div className={cfg.errHint} style={{ margin: "0 8px" }}>{sa.hint}</div> : null}
        <DictSection rows={consRows} {...secProps("c")} />
        <DictSection rows={resRows} {...secProps("r")} />
        <BranchDaysSection branches={branches} />{/* 05.10: часы работы филиала и общий расход */}
      </div>
      {tip ? (
        <TipBox anchor={tip.anchor} onClose={() => setTip(null)}>
          <DictTips kind={tip.k} />
        </TipBox>
      ) : null}
      {g ? (
        <CfgModal
          tone="danger"
          title={`Используется в ${g.count} ${g.count === 1 ? "месте" : "местах"}`}
          actionLabel="Всё равно удалить"
          actionClassName={css.dgWide}
          onAction={() => { killRow(g.kind, g.id); sa.forceDelete(g); }}
          onCancel={sa.clearGuard}
        >
          «<b className={cfg.mName}>{g.name}</b>» привязан к:{" "}
          <span className={css.uses}>
            {g.uses.map((n, i) => (
              <span key={i}>{i > 0 ? ", " : ""}<b>{n}</b></span>
            ))}
            {g.count > 3 ? ` и ещё ${g.count - 3}` : ""}
          </span>
          <span className={cfg.mSub}>
            Нормы и привязки исчезнут из этих мест. Исторические отчёты не меняются.
          </span>
        </CfgModal>
      ) : null}
      {snack.element}
    </>
  );
}
